'use strict';

const libPictView = require('pict-view');

/**
 * Documentation — the #/Documentation management screen. A left rail browses every platform help topic (grouped by
 * feature area, with a per-topic status chip: platform default vs. this customer's own copy vs. a customer topic);
 * the main pane views + edits the selected topic through the DocumentationProvider, which mounts the markdown
 * editor and writes to AcademyContent (the same path the F1 panel editor uses). The shell is static; all content
 * is assigned into #Doc-Rail / #Doc-Detail by the provider (SamplePlan hand-built-HTML pattern).
 */
class Documentation extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider() { return this.pict.providers.Documentation; }

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		const tmpProvider = this.provider;
		if (tmpProvider)
		{
			tmpProvider.render();
			if (!tmpProvider.state.Loaded && !tmpProvider.state.Loading) { tmpProvider.loadAsync(); }
		}
		return super.onAfterRender(pRenderable);
	}
}

module.exports = Documentation;
module.exports.default_configuration =
{
	ViewIdentifier: 'Documentation',
	DefaultRenderable: 'DocumentationWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.doc-wrap { padding: 1.25rem 1.5rem 2rem; }
		.doc-head { margin: 0 0 1rem; }
		.doc-title { font-size:1.5rem; font-weight:700; line-height:1.2; margin:0; color:var(--theme-color-text-primary,#1f2733); }
		.doc-subtitle { font-size:0.9rem; color:var(--theme-color-text-muted,#6b7686); margin:0.2rem 0 0; }
		.doc-cols { display:grid; grid-template-columns: 17rem 1fr; gap:1.1rem; align-items:start; }
		@media (max-width: 820px) { .doc-cols { grid-template-columns: 1fr; } }
		.doc-rail { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:0.6rem; }
		.doc-rail-loading { color:var(--theme-color-text-muted,#6b7686); font-style:italic; padding:0.6rem; }
		.doc-rail-group { margin:0 0 0.8rem; }
		.doc-rail-head { font-size:0.66rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-text-muted,#6b7686); padding:0.25rem 0.5rem; }
		.doc-rail-item { display:flex; align-items:center; gap:0.4rem; padding:0.38rem 0.55rem; border-radius:6px; font-size:0.88rem; color:var(--theme-color-text-secondary,#45505f); cursor:pointer; }
		.doc-rail-item:hover { background:var(--theme-color-background-secondary,#eef1f6); color:var(--theme-color-text-primary,#1f2733); }
		.doc-rail-item.is-active { background:var(--theme-color-brand-primary,#2e7d74); color:#fff; font-weight:600; }
		.doc-rail-item-label { flex:1 1 auto; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
		.doc-rail-add { font-size:0.78rem; font-weight:600; color:var(--theme-color-brand-primary,#2e7d74); cursor:pointer; padding:0.32rem 0.55rem; }
		.doc-rail-add:hover { text-decoration:underline; }
		.doc-chip { font-size:0.58rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; padding:0.08rem 0.4rem; border-radius:9px; line-height:1.7; white-space:nowrap; }
		.doc-chip-default, .doc-chip-canonical { color:var(--theme-color-text-muted,#6b7686); background:var(--theme-color-background-tertiary,#eef1f6); }
		.doc-chip-custom { color:#fff; background:var(--theme-color-primary,#2f6fed); }
		.doc-rail-item.is-active .doc-chip { color:#fff; background:rgba(255,255,255,0.25); }
		.doc-main { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:1.1rem 1.25rem; min-height:22rem; }
		.doc-detail-head { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; flex-wrap:wrap; margin:0 0 0.7rem; }
		.doc-detail-title { font-size:1.15rem; font-weight:700; color:var(--theme-color-text-primary,#1f2733); }
		.doc-detail-sub { display:flex; align-items:center; gap:0.5rem; margin-top:0.25rem; }
		.doc-detail-where { font-size:0.78rem; color:var(--theme-color-text-muted,#6b7686); }
		.doc-detail-where code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size:0.72rem; }
		.doc-detail-note { font-size:0.84rem; color:var(--theme-color-text-muted,#6b7686); margin:0 0 0.7rem; }
		.doc-detail-actions { display:flex; align-items:center; gap:0.5rem; flex-wrap:wrap; }
		.doc-delrow { margin-top:0.9rem; }
		.doc-detail-body { font-size:0.92rem; line-height:1.6; color:var(--theme-color-text-secondary,#45505f); }
			/* Markdown typography — mirrors the F1/? help panel so rendered docs read the same here (headers with
			   underline rules, spacing, lists, tables, code) instead of an unstyled run-on block. */
			.doc-detail-body > *:first-child { margin-top:0; }
			.doc-detail-body h1, .doc-detail-body h2, .doc-detail-body h3, .doc-detail-body h4 { color:var(--theme-color-text-primary,#1f2733); font-weight:700; line-height:1.25; margin:1.4rem 0 0.6rem; }
			.doc-detail-body h1 { font-size:1.4rem; padding-bottom:0.3rem; border-bottom:1px solid var(--theme-color-border-default,#d7dce3); }
			.doc-detail-body h2 { font-size:1.15rem; padding-bottom:0.25rem; border-bottom:1px solid var(--theme-color-border-light,#e8ebf0); }
			.doc-detail-body h3 { font-size:1.02rem; }
			.doc-detail-body h4 { font-size:0.92rem; }
			.doc-detail-body p { margin:0.6rem 0; }
			.doc-detail-body ul, .doc-detail-body ol { margin:0.55rem 0; padding-left:1.5rem; }
			.doc-detail-body li { margin:0.25rem 0; }
			.doc-detail-body li > ul, .doc-detail-body li > ol { margin:0.2rem 0; }
			.doc-detail-body strong { color:var(--theme-color-text-primary,#1f2733); font-weight:650; }
			.doc-detail-body a { color:var(--theme-color-primary,#2f6fed); text-decoration:none; }
			.doc-detail-body a:hover { text-decoration:underline; }
			.doc-detail-body code { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:0.85em; background:var(--theme-color-background-tertiary,#eef1f6); padding:0.1rem 0.35rem; border-radius:4px; }
			.doc-detail-body pre { background:var(--theme-color-background-tertiary,#eef1f6); padding:0.8rem 1rem; border-radius:8px; overflow-x:auto; margin:0.7rem 0; }
			.doc-detail-body pre code { background:none; padding:0; font-size:0.82rem; }
			.doc-detail-body blockquote { margin:0.7rem 0; padding:0.3rem 0.9rem; border-left:3px solid var(--theme-color-border-default,#d7dce3); color:var(--theme-color-text-muted,#6b7686); }
			.doc-detail-body hr { border:0; border-top:1px solid var(--theme-color-border-default,#d7dce3); margin:1.2rem 0; }
			.doc-detail-body table { border-collapse:collapse; width:100%; margin:0.8rem 0; font-size:0.86rem; display:block; overflow-x:auto; }
			.doc-detail-body th, .doc-detail-body td { border:1px solid var(--theme-color-border-light,#e8ebf0); padding:0.4rem 0.6rem; text-align:left; vertical-align:top; }
			.doc-detail-body th { background:var(--theme-color-background-secondary,#f7f8fa); font-weight:650; color:var(--theme-color-text-primary,#1f2733); }
			.doc-detail-body img { max-width:100%; }
		.doc-editor-host { border:1px solid var(--theme-color-border-light,#e8ebf0); border-radius:7px; min-height:18rem; }
		.doc-input { flex:1 1 16rem; font:inherit; font-size:0.9rem; padding:0.4rem 0.55rem; border-radius:6px; border:1px solid var(--theme-color-border-medium,#d5dbe3); background:var(--theme-color-background-primary,#fff); color:var(--theme-color-text-primary,#1f2733); }
		.doc-btn { font-size:0.84rem; font-weight:600; padding:0.42rem 0.9rem; border-radius:6px; cursor:pointer; border:1px solid transparent; }
		.doc-btn-primary { color:#fff; background:var(--theme-color-primary,#2f6fed); }
		.doc-btn-ghost { color:var(--theme-color-text,#22303f); background:transparent; border-color:var(--theme-color-border-medium,#d5dbe3); }
		.doc-btn-danger { color:var(--theme-color-status-danger,#c0392b); background:transparent; border-color:var(--theme-color-status-danger,#c0392b); }
		.doc-empty { padding:3rem 1rem; text-align:center; color:var(--theme-color-text-muted,#6b7686); }
		.doc-error { font-size:0.85rem; color:var(--theme-color-status-danger,#c0392b); margin:0 0 0.6rem; }
	`,
	Templates:
	[
		{
			Hash: 'Documentation-Wrap',
			Template: /*html*/`
				<div class="doc-wrap">
					<div class="doc-head">
						<h1 class="doc-title">Documentation</h1>
						<p class="doc-subtitle">Manage the platform's in-app help. Headlight administrators edit the platform-default topics; customer administrators customize their own copy of any topic (and add their own). Changes appear in the F1 / ? help right away.</p>
					</div>
					<div class="doc-cols">
						<div id="Doc-Rail" class="doc-rail"></div>
						<div id="Doc-Detail" class="doc-main"></div>
					</div>
				</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'DocumentationWrap',
			TemplateHash: 'Documentation-Wrap',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
			RenderMethod: 'replace',
		},
	],
};
